import {
  Box,
  Button,
  Checkbox,
  Modal,
  Select,
  SegmentedControl,
  Switch,
  Tabs,
  Text,
} from "@mantine/core";
import {
  IconExternalLink,
  IconInfoCircle,
  IconMail,
} from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getAllStaff } from "../../../../services/staff";
import { saveDigestSettings } from "../../../../services/digest";
import useStyles from "./style";

const RECIPIENTS = [
  { initials: "J", name: "Jane Cooper" },
  { initials: "M", name: "Marcus Avery" },
  { initials: "P", name: "Priya Singh" },
  { initials: "T", name: "Tom Reyes" },
];

const SECTIONS = [
  {
    key: "staffLogin",
    title: "Staff login summary",
    desc: "Who logged in yesterday, with expected staff flagged if missing.",
    example: "6 of 8 staff logged in · 2 missed",
    badge: null,
  },
  {
    key: "staffDocs",
    title: "Staff documentation activity",
    desc: "Per-staff counts for Notes, ADL tasks and MAR entries.",
    example: "14 notes · 62 ADLs · 38 MAR",
    badge: null,
  },
  {
    key: "missingDocs",
    title: "Missing documentation alerts",
    desc: "Clients flagged by Command Center rules — only included when gaps exist.",
    example: "3 clients · 5 gaps",
    badge: "Requires Command Center",
  },
];

const TIME_OPTIONS = [
  { value: "5:00 AM", label: "5:00 AM" },
  { value: "6:00 AM", label: "6:00 AM" },
  { value: "7:00 AM", label: "7:00 AM" },
  { value: "8:00 AM", label: "8:00 AM" },
  { value: "9:00 AM", label: "9:00 AM" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/Los_Angeles", label: "America / Los Angeles" },
  { value: "America/New_York", label: "America / New York" },
  { value: "America/Chicago", label: "America / Chicago" },
  { value: "America/Denver", label: "America / Denver" },
];

const buildDigestPreviewHTML = ({
  staffLogin,
  staffDocs,
  missingDocs,
}) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1.0"/>
  <title>Daily Digest Preview</title>
  <style>
    body{margin:0;padding:16px;background:#ffffff;font-family:Inter,Arial,sans-serif;}
    *{box-sizing:border-box;}
  </style>
</head>
<body>
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:640px;margin:0 auto;background:#ffffff;border-radius:16px;border:1px solid #E5E5E5;overflow:hidden;box-shadow:0 8px 24px -8px rgba(16,24,40,0.08);">
  <!-- NAV -->
  <tr>
    <td style="padding:21px 32px;border-bottom:1px solid #F2F2F2;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
        <td><img src="https://ogiso-bucket.s3.us-east-2.amazonaws.com/logo.png" alt="Ogiso" height="32" style="display:block;" /></td>
        <td align="right">
          <span style="display:inline-block;width:6px;height:6px;background:#FFBA45;border-radius:3px;vertical-align:middle;margin-right:6px;"></span>
          <span style="font-family:Inter,Arial,sans-serif;font-size:12px;font-weight:500;color:#737373;">Daily Digest</span>
        </td>
      </tr></table>
    </td>
  </tr>
  <!-- HERO -->
  <tr>
    <td style="padding:28px 33px 20px;">
      <div style="font-size:12px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.72px;margin-bottom:8px;">Yesterday at a glance</div>
      <div style="font-size:28px;font-weight:700;color:#171717;line-height:1.2;margin-bottom:10px;">Good morning, Jane.</div>
      <div style="font-size:15px;color:#525252;line-height:1.55;">Here&rsquo;s what happened across your organization on <strong style="color:#171717;">Thursday, May 10</strong>. Two clients have documentation gaps that need a closer look.</div>
    </td>
  </tr>
  <!-- STATS -->
  <tr>
    <td style="padding:0 33px 24px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
        <td width="25%" style="padding-right:4px;">
          <div style="background:#FAFAFA;border:1px solid #F2F2F2;border-radius:10px;padding:13px 15px 10px;">
            <span style="font-size:22px;font-weight:700;color:#171717;">9</span>
            <span style="font-size:13px;font-weight:500;color:#A3A3A3;margin-left:3px;">/ 11</span>
            <div style="font-size:11px;font-weight:500;color:#737373;margin-top:4px;">Staff active</div>
          </div>
        </td>
        <td width="25%" style="padding:0 4px;">
          <div style="background:#FAFAFA;border:1px solid #F2F2F2;border-radius:10px;padding:13px 15px 10px;">
            <div style="font-size:22px;font-weight:700;color:#171717;">14</div>
            <div style="font-size:11px;font-weight:500;color:#737373;margin-top:4px;">Notes</div>
          </div>
        </td>
        <td width="25%" style="padding:0 4px;">
          <div style="background:#FAFAFA;border:1px solid #F2F2F2;border-radius:10px;padding:13px 15px 10px;">
            <div style="font-size:22px;font-weight:700;color:#171717;">62</div>
            <div style="font-size:11px;font-weight:500;color:#737373;margin-top:4px;">ADL tasks</div>
          </div>
        </td>
        <td width="25%" style="padding-left:4px;">
          <div style="background:#FAFAFA;border:1px solid #F2F2F2;border-radius:10px;padding:13px 15px 10px;">
            <div style="font-size:22px;font-weight:700;color:#171717;">38</div>
            <div style="font-size:11px;font-weight:500;color:#737373;margin-top:4px;">MAR entries</div>
          </div>
        </td>
      </tr></table>
    </td>
  </tr>
  ${
    missingDocs
      ? `<!-- COMPLIANCE -->
  <tr>
    <td style="padding:0 33px 20px;">
      <div style="padding-left:8px;border-left:4px solid #F04438;margin-bottom:14px;">
        <div style="font-size:11px;font-weight:600;color:#B42318;text-transform:uppercase;letter-spacing:0.88px;">Compliance Alerts</div>
        <div style="font-size:18px;font-weight:700;color:#171717;">Missing documentation</div>
        <div style="font-size:13px;color:#737373;">Flagged by Command Center compliance rules</div>
      </div>
      <div style="background:#FEF3F2;border:1px solid #FECDCA;border-radius:10px;padding:13px 15px;margin-bottom:10px;">
        <div style="font-size:12.5px;font-weight:700;color:#7A271A;">3 clients &middot; 5 gaps total</div>
        <div style="font-size:12.5px;color:#B42318;margin-top:4px;">Based on your Command Center rules: progress note required daily, ADL completion &ge; 80%, MAR pass-rate = 100%.</div>
      </div>
      <div style="border:1px solid #E5E5E5;border-radius:12px;overflow:hidden;margin-bottom:8px;">
        <div style="padding:14px 17px;border-bottom:1px solid #F2F2F2;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:14px;font-weight:600;color:#171717;">Missing Notes</div>
            <div style="font-size:12px;color:#737373;">2 clients affected</div>
          </div>
          <span style="background:#FEF3F2;border-radius:999px;padding:3px 8px;font-size:11px;font-weight:700;color:#B42318;">2</span>
        </div>
        <div style="background:#FAFAFA;padding:0 8px;">
          <div style="display:flex;align-items:center;padding:12px 0;gap:10px;">
            <div style="width:24px;height:24px;border-radius:12px;background:#FFF3DC;text-align:center;line-height:24px;font-size:11px;font-weight:700;color:#7A271A;flex-shrink:0;">S</div>
            <div>
              <span style="font-size:13px;font-weight:600;color:#171717;">Sarah Mitchell</span>
              <span style="font-size:11px;color:#A3A3A3;margin-left:4px;">&middot; C-1024</span>
              <div style="font-size:12px;color:#737373;">No progress note yesterday</div>
            </div>
          </div>
          <div style="border-top:1px solid #F2F2F2;display:flex;align-items:center;padding:12px 0;gap:10px;">
            <div style="width:24px;height:24px;border-radius:12px;background:#FFF3DC;text-align:center;line-height:24px;font-size:11px;font-weight:700;color:#7A271A;flex-shrink:0;">M</div>
            <div>
              <span style="font-size:13px;font-weight:600;color:#171717;">Marcus Lee</span>
              <span style="font-size:11px;color:#A3A3A3;margin-left:4px;">&middot; C-2017</span>
              <div style="font-size:12px;color:#737373;">No progress note yesterday</div>
            </div>
          </div>
        </div>
        <div style="padding:10px 17px;"><a style="font-size:13px;font-weight:600;color:#171717;text-decoration:none;">Review Missing Notes &rarr;</a></div>
      </div>
      <div style="border:1px solid #E5E5E5;border-radius:12px;overflow:hidden;margin-bottom:8px;">
        <div style="padding:14px 17px;border-bottom:1px solid #F2F2F2;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:14px;font-weight:600;color:#171717;">Missing ADL Tasks</div>
            <div style="font-size:12px;color:#737373;">1 client affected</div>
          </div>
          <span style="background:#FEF3F2;border-radius:999px;padding:3px 8px;font-size:11px;font-weight:700;color:#B42318;">1</span>
        </div>
        <div style="background:#FAFAFA;padding:0 8px;">
          <div style="display:flex;align-items:center;padding:12px 0;gap:10px;">
            <div style="width:24px;height:24px;border-radius:12px;background:#FFF3DC;text-align:center;line-height:24px;font-size:11px;font-weight:700;color:#7A271A;flex-shrink:0;">S</div>
            <div>
              <span style="font-size:13px;font-weight:600;color:#171717;">Sarah Mitchell</span>
              <span style="font-size:11px;color:#A3A3A3;margin-left:4px;">&middot; C-1024</span>
              <div style="font-size:12px;color:#737373;">3 of 6 tasks unrecorded</div>
            </div>
          </div>
        </div>
        <div style="padding:10px 17px;"><a style="font-size:13px;font-weight:600;color:#171717;text-decoration:none;">Review Missing ADLs &rarr;</a></div>
      </div>
      <div style="border:1px solid #E5E5E5;border-radius:12px;overflow:hidden;">
        <div style="padding:14px 17px;border-bottom:1px solid #F2F2F2;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:14px;font-weight:600;color:#171717;">Missing MAR Entries</div>
            <div style="font-size:12px;color:#737373;">2 clients affected</div>
          </div>
          <span style="background:#FEF3F2;border-radius:999px;padding:3px 8px;font-size:11px;font-weight:700;color:#B42318;">2</span>
        </div>
        <div style="background:#FAFAFA;padding:0 8px;">
          <div style="display:flex;align-items:center;padding:12px 0;gap:10px;">
            <div style="width:24px;height:24px;border-radius:12px;background:#FFF3DC;text-align:center;line-height:24px;font-size:11px;font-weight:700;color:#7A271A;flex-shrink:0;">A</div>
            <div>
              <span style="font-size:13px;font-weight:600;color:#171717;">Anna Doe</span>
              <span style="font-size:11px;color:#A3A3A3;margin-left:4px;">&middot; C-3041</span>
              <div style="font-size:12px;color:#737373;">2 missed administration windows</div>
            </div>
          </div>
          <div style="border-top:1px solid #F2F2F2;display:flex;align-items:center;padding:12px 0;gap:10px;">
            <div style="width:24px;height:24px;border-radius:12px;background:#FFF3DC;text-align:center;line-height:24px;font-size:11px;font-weight:700;color:#7A271A;flex-shrink:0;">S</div>
            <div>
              <span style="font-size:13px;font-weight:600;color:#171717;">Sarah Mitchell</span>
              <span style="font-size:11px;color:#A3A3A3;margin-left:4px;">&middot; C-1024</span>
              <div style="font-size:12px;color:#737373;">1 missed administration window</div>
            </div>
          </div>
        </div>
        <div style="padding:10px 17px;"><a style="font-size:13px;font-weight:600;color:#171717;text-decoration:none;">Review Missing MAR &rarr;</a></div>
      </div>
    </td>
  </tr>`
      : ""
  }
  ${
    staffLogin
      ? `<!-- SECTION 1: STAFF LOGIN -->
  <tr>
    <td style="padding:0 33px 8px;">
      <div style="font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.88px;margin-bottom:4px;">Section 1</div>
      <div style="font-size:18px;font-weight:700;color:#171717;margin-bottom:2px;">Staff login activity</div>
      <div style="font-size:13px;color:#737373;margin-bottom:14px;">9 of 11 expected staff logged in</div>
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #E5E5E5;border-radius:12px;overflow:hidden;">
        <tr style="background:#FAFAFA;">
          <td colspan="2" style="padding:10px 14px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Staff</td>
          <td style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Role</td>
          <td style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Session</td>
          <td align="right" style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Logins</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td width="28" style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">R</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Renee Park</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">Direct Support</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">08:14 &rarr; 17:02</td>
          <td align="right" style="padding:12px 8px;font-size:13px;font-weight:700;color:#171717;">2</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">T</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Tom Peterson</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">RN</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">07:30 &rarr; 15:45</td>
          <td align="right" style="padding:12px 8px;font-size:13px;font-weight:700;color:#171717;">1</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">M</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Maria Silva</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">Direct Support</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;">06:55 &rarr; 19:10</td>
          <td align="right" style="padding:12px 8px;font-size:13px;font-weight:700;color:#171717;">3</td>
        </tr>
        <tr style="background:#FFFBEB;border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FEF0C7;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#B54708;">D</div></td>
          <td style="padding:12px 8px;vertical-align:top;">
            <div style="font-size:13px;font-weight:600;color:#171717;">David Chen</div>
            <div style="font-size:11px;font-weight:500;color:#B54708;">Did not log in</div>
          </td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;vertical-align:middle;">RN</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;vertical-align:middle;">Expected</td>
          <td align="right" style="padding:12px 8px;vertical-align:middle;"><span style="background:#FEF0C7;border-radius:999px;padding:2px 8px;font-size:12px;font-weight:700;color:#B54708;">0</span></td>
        </tr>
        <tr style="background:#FFFBEB;border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FEF0C7;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#B54708;">O</div></td>
          <td style="padding:12px 8px;vertical-align:top;">
            <div style="font-size:13px;font-weight:600;color:#171717;">Olivia Hart</div>
            <div style="font-size:11px;font-weight:500;color:#B54708;">Did not log in</div>
          </td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;vertical-align:middle;">Direct Support</td>
          <td style="padding:12px 8px;font-size:12px;color:#525252;vertical-align:middle;">Expected</td>
          <td align="right" style="padding:12px 8px;vertical-align:middle;"><span style="background:#FEF0C7;border-radius:999px;padding:2px 8px;font-size:12px;font-weight:700;color:#B54708;">0</span></td>
        </tr>
      </table>
      <div style="padding:12px 0 4px;"><a style="font-size:13px;font-weight:600;color:#171717;text-decoration:none;">View staff activity details &rarr;</a></div>
    </td>
  </tr>`
      : ""
  }
  ${
    staffDocs
      ? `<!-- SECTION 2: DOCUMENTATION -->
  <tr>
    <td style="padding:16px 33px 8px;">
      <div style="font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.88px;margin-bottom:4px;">Section 2</div>
      <div style="font-size:18px;font-weight:700;color:#171717;margin-bottom:2px;">Documentation activity</div>
      <div style="font-size:13px;color:#737373;margin-bottom:14px;">Per-staff breakdown of notes, ADLs and MAR entries</div>
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #E5E5E5;border-radius:12px;overflow:hidden;">
        <tr style="background:#FAFAFA;">
          <td colspan="2" style="padding:10px 14px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Staff</td>
          <td style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">Notes created</td>
          <td align="right" style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">ADLs</td>
          <td align="right" style="padding:10px 8px;font-size:11px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.66px;border-bottom:1px solid #F2F2F2;">MAR</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td width="28" style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">R</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Renee Park</td>
          <td style="padding:12px 8px;font-size:13px;color:#171717;"><span style="font-weight:600;">4 </span><span style="color:#737373;font-weight:500;">notes</span></td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">18</td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">9</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">T</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Tom Peterson</td>
          <td style="padding:12px 8px;font-size:13px;color:#171717;"><span style="font-weight:600;">3 </span><span style="color:#737373;font-weight:500;">notes</span></td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">12</td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">14</td>
        </tr>
        <tr style="border-top:1px solid #F5F5F5;">
          <td style="padding:12px 0 12px 14px;"><div style="width:28px;height:28px;border-radius:14px;background:#FFF3DC;text-align:center;line-height:28px;font-size:12px;font-weight:700;color:#7A271A;">M</div></td>
          <td style="padding:12px 8px;font-size:13px;font-weight:600;color:#171717;">Maria Silva</td>
          <td style="padding:12px 8px;font-size:13px;color:#171717;"><span style="font-weight:600;">5 </span><span style="color:#737373;font-weight:500;">notes</span></td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">22</td>
          <td align="right" style="padding:12px 8px;font-size:14px;font-weight:700;color:#171717;">8</td>
        </tr>
      </table>
    </td>
  </tr>`
      : ""
  }
  <!-- CTA -->
  <tr>
    <td style="padding:24px 33px 16px;">
      <div style="background:#171717;border-radius:12px;padding:20px 22px;display:flex;align-items:center;justify-content:space-between;">
        <div>
          <div style="font-size:14px;font-weight:600;color:#fff;">Open the full report in Ogiso</div>
          <div style="font-size:12px;color:rgba(255,255,255,0.6);margin-top:2px;">Drill into any staff member, client or facility.</div>
        </div>
        <a style="display:inline-block;background:#FFBA45;color:#171717;border-radius:8px;padding:12px 18px;text-decoration:none;font-size:14px;font-weight:600;white-space:nowrap;margin-left:16px;">Go to Command Center &rarr;</a>
      </div>
    </td>
  </tr>
  <!-- FOOTER -->
  <tr>
    <td style="padding:16px 33px 20px;border-top:1px solid #F2F2F2;background:#FAFAFA;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="font-size:12px;color:#737373;">Sent to Admins of <strong style="color:#404040;">Lakeside Behavioral Health</strong></td>
        <td align="right">
          <span style="font-size:12px;color:#737373;margin-right:16px;">Digest settings</span>
          <span style="font-size:12px;color:#737373;">Unsubscribe</span>
        </td>
      </tr></table>
    </td>
  </tr>
</table>
</body>
</html>`;

const Notifications = () => {
  const { classes } = useStyles();

  const allStaff = useSelector((state) => state?.staff?.allStaff);
  const profile = useSelector((state) => state?.profile?.profile);
  const adminRecipients = (allStaff || []).filter(
    (s) => s?.type === "staff" && s?.role === "admin",
  );

  const [activeTab, setActiveTab] = useState("daily-digest");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState("7:00 AM");
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [frequency, setFrequency] = useState("daily");
  const [checkedSections, setCheckedSections] = useState({
    staffLogin: true,
    staffDocs: true,
    missingDocs: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allStaff?.length) getAllStaff("withAdmin");
  }, []);

  useEffect(() => {
    if (!profile) return;
    setSubscribed(profile?.user?.digestEnabled ?? false);
    if (profile?.user?.digestFrequency) {
      setFrequency(profile.user.digestFrequency);
    }
    const sections = profile?.user?.digestSections;
    if (sections) {
      setCheckedSections({
        staffLogin: sections.staffLogin ?? true,
        staffDocs: sections.staffDocs ?? true,
        missingDocs: sections.missingDocs ?? true,
      });
    }
  }, [profile]);

  const toggleSection = (key) =>
    setCheckedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setSaving(true);
    await saveDigestSettings({
      digestEnabled: subscribed,
      digestFrequency: frequency,
      digestSections: checkedSections,
    });
    setSaving(false);
  };

  return (
    <Box mt={16}>
      {/* ── Email Preview Modal ── */}
      <Modal
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Daily Digest — Preview"
        size="700px"
        zIndex={1000}
        className={classes.previewModal}
      >
        <Box className={classes.previewIframeWrap}>
          <iframe
            srcDoc={buildDigestPreviewHTML(checkedSections)}
            title="Daily Digest Preview"
            className={classes.previewIframe}
            sandbox="allow-same-origin"
          />
        </Box>
      </Modal>

      {/* ── Tab bar ── */}
      <Tabs
        value={activeTab}
        onTabChange={setActiveTab}
        className={classes.tabBar}
      >
        <Tabs.List>
          <Tabs.Tab value="daily-digest">Daily Digest</Tabs.Tab>
          {/* <Tabs.Tab value="in-app">In-app</Tabs.Tab>
          <Tabs.Tab value="email-alerts">Email alerts</Tabs.Tab>
          <Tabs.Tab value="sms">SMS</Tabs.Tab> */}
        </Tabs.List>
      </Tabs>

      {/* ── Daily Digest panel ── */}
      {activeTab === "daily-digest" && (
        <>
          {/* Main card */}
          <Box className={classes.card} mt={16}>
            {/* Header */}
            <Box className={classes.cardHeader}>
              <Box className={classes.cardHeaderLeft}>
                <Box className={classes.cardIconWrap}>
                  <IconMail size={22} color="#B54708" stroke={1.5} />
                </Box>
                <Box>
                  <Text className={classes.cardTitle}>Daily Digest</Text>
                  <Text className={classes.cardDesc}>
                    A once-daily email summarizing staff logins, documentation
                    activity
                    <br />
                    and compliance gaps from the previous day. Sent to all
                    Admins.
                  </Text>
                </Box>
              </Box>
              <Button
                className={classes.previewBtn}
                leftIcon={<IconMail size={16} color="#525252" stroke={1.5} />}
                variant="default"
                onClick={() => setPreviewOpen(true)}
              >
                Preview email
              </Button>
            </Box>
            {/* Toggle row */}
            <Box className={classes.row}>
              <Box>
                <Text className={classes.rowLabel}>
                  Send me the Daily Digest
                </Text>
                <Text className={classes.rowSub}>
                  You&apos;ll receive your digest by 7:00 AM each morning.
                </Text>
              </Box>
              <Switch
                checked={subscribed}
                onChange={(e) => setSubscribed(e.currentTarget.checked)}
                size="md"
                color="gray"
                // className={classes.toggleTrack}
              />
            </Box>
            {/* Disabled overlay wraps everything below the toggle */}
            <Box className={classes.disabledSection}>
              {!subscribed && <Box className={classes.disabledOverlay} />}

              {/* Delivery time row */}
              {/* <Box className={classes.row}>
                <Box>
                  <Text className={classes.rowLabel}>Delivery time</Text>
                  <Text className={classes.rowSub}>
                    When the digest is sent each day. We&apos;ll convert to
                    <br />
                    your timezone.
                  </Text>
                </Box>
                <Box className={classes.rowRight}>
                   <Select
                    data={TIME_OPTIONS}
                    value={deliveryTime}
                    onChange={setDeliveryTime}
                    className={classes.selectInput}
                    styles={{ input: { width: 120 } }}
                    withinPortal
                  />
                  <Select
                    data={TIMEZONE_OPTIONS}
                    value={timezone}
                    onChange={setTimezone}
                    className={classes.selectInput}
                    styles={{ input: { width: 200 } }}
                    withinPortal
                  /> 
                </Box>
              </Box> */}

              {/* Frequency row */}
              <Box className={classes.row}>
                <Box>
                  <Text className={classes.rowLabel}>Frequency</Text>
                  <Text className={classes.rowSub}>
                    Weekdays only is useful if your facility doesn&apos;t
                    operate on
                    <br />
                    weekends.
                  </Text>
                </Box>
                <SegmentedControl
                  value={frequency}
                  onChange={setFrequency}
                  className={classes.segmented}
                  data={[
                    { label: "Every day", value: "daily" },
                    { label: "Weekdays only", value: "weekdays" },
                  ]}
                />
              </Box>

              {/* Sections */}
              <Box className={classes.sectionsRow}>
                <Text className={classes.sectionLabel}>Sections</Text>
                <Text className={classes.sectionSubLabel}>
                  Choose which sections appear in your email.
                </Text>

                {SECTIONS.map((s) => (
                  <Box key={s.key} className={classes.sectionCard}>
                    <Checkbox
                      checked={checkedSections[s.key]}
                      onChange={() => toggleSection(s.key)}
                      className={classes.sectionCheckbox}
                      mt={2}
                    />
                    <Box className={classes.sectionCardBody}>
                      <Box className={classes.sectionCardTitle}>
                        {s.title}
                        {s.badge && (
                          <Text
                            component="span"
                            className={classes.requiresBadge}
                          >
                            {s.badge}
                          </Text>
                        )}
                      </Box>
                      <Text className={classes.sectionCardDesc}>{s.desc}</Text>
                      <Box className={classes.examplePill}>
                        <Text component="span" className={classes.exampleLabel}>
                          Example:&nbsp;
                        </Text>
                        <Text component="span" className={classes.exampleValue}>
                          {s.example}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Recipients */}
              <Box className={classes.recipientsRow}>
                <Box className={classes.recipientsHeader}>
                  <Text className={classes.recipientsTitle}>Recipients</Text>
                  {/* <Text className={classes.manageLink}>Manage admins →</Text> */}
                </Box>
                <Text className={classes.recipientsDesc}>
                  All users with the{" "}
                  <Text component="span" className={classes.recipientsDescBold}>
                    Administrator
                  </Text>{" "}
                  role automatically receive this digest.
                </Text>
                <Box className={classes.recipientPills}>
                  {adminRecipients.map((r) => {
                    const displayName =
                      r.name ||
                      `${r.firstName || ""} ${r.lastName || ""}`.trim() ||
                      "Admin";
                    const initial = displayName[0].toUpperCase();
                    return (
                      <Box key={r._id} className={classes.recipientPill}>
                        <Box className={classes.recipientAvatar}>{initial}</Box>
                        {displayName}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

            </Box>{" "}
            {/* end disabledSection */}

            {/* Footer */}
            <Box className={classes.cardFooter}>
              {/* <Box
                component="a"
                href="#"
                className={classes.learnLink}
                onClick={(e) => e.preventDefault()}
              >
                <IconExternalLink size={14} color="#737373" stroke={1.2} />
                Learn what&apos;s in the Daily Digest
              </Box> */}
              <Box className={classes.footerBtns}>
                {/* <Button className={classes.testBtn} variant="default">
                  Send test email
                </Button> */}
                <Button
                  className={classes.saveBtn}
                  variant="filled"
                  onClick={handleSave}
                  loading={saving}
                >
                  Save changes
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Info banner */}
          {/* <Box className={classes.infoBanner}>
            <Box className={classes.infoBannerIcon}>
              <IconInfoCircle size={18} color="#175CD3" stroke={1.5} />
            </Box>
            <Text className={classes.infoBannerText}>
              <Text component="span" className={classes.infoBannerBold}>
                The Missing documentation section only appears when Command
                Center is enabled.
              </Text>{" "}
              You currently have{" "}
              <Text component="span" className={classes.infoBannerBold}>
                3 active compliance rules
              </Text>
              . Configure rules in{" "}
              <Text component="span" className={classes.infoBannerLink}>
                Command Center settings
              </Text>
              .
            </Text>
          </Box> */}
        </>
      )}

      {activeTab === "in-app" && (
        <Box className={classes.card} mt={16} p={24}>
          <Text c="#737373" size="sm" ff="Inter">
            In-app notification settings coming soon.
          </Text>
        </Box>
      )}
      {activeTab === "email-alerts" && (
        <Box className={classes.card} mt={16} p={24}>
          <Text c="#737373" size="sm" ff="Inter">
            Email alert settings coming soon.
          </Text>
        </Box>
      )}
      {activeTab === "sms" && (
        <Box className={classes.card} mt={16} p={24}>
          <Text c="#737373" size="sm" ff="Inter">
            SMS notification settings coming soon.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Notifications;
